/**
 * Direct per-entity RDF writer — the WRITE side of the one-document-per-entity
 * model (rule_document-per-entity), decoupled from the reactive read.
 *
 * WHY a direct write (not the reactive `ngSet.add`). Each entity is its OWN
 * document (`createEntityDoc(scope)`). To WRITE an entity through the reactive
 * ORM set, its document must already be in the set's SUBSCRIPTION scope
 * (`useShape(shape, { graphs })`) — otherwise the set is "readonly because scope
 * is empty" (against the real broker) and the target repo isn't opened for the
 * write. Registering a freshly-created document into that scope is React state,
 * so it only takes effect on the NEXT render — you cannot create-then-add in one
 * synchronous pass (seed loops, first create). The fake-ng unit tests missed this
 * because they allow adds regardless of scope.
 *
 * So the entity RDF is written STRAIGHT into its own document via the SDK's
 * `docs.sparqlUpdate` primitive (the real injected `ng`) — the same direct-write
 * path `insertNotification` already uses. The document was just created and is
 * openable, so the write lands immediately. The READ stays reactive: the document
 * NURI joins the scope's reactive view, which reads the entity back. Write
 * (direct, per-document) and read (reactive) are decoupled — the model (one
 * document per entity, per-document isolation) is unchanged.
 *
 * TYPED TERMS. The ORM reads back via the SHEX shapes (festipodShapes.shex), so
 * each field must be written with the RIGHT RDF term: xsd:integer/float/boolean
 * for the numeric/boolean fields, an IRI (`<…>`) for the reference fields
 * (Participation.event / .user), a plain string literal otherwise. A field
 * written with the wrong term (e.g. participantCount as a bare string) does not
 * round-trip through the shape.
 */

import { docs } from '@ng-eventually/polyfill';
import type { Nuri, NuriLike } from '@ng-eventually/polyfill';
import { sessionPromise } from '../utils/ngSession';
import { escapeLiteral, escapeIri } from './sparqlEscape';

/** The RDF `@type` IRIs of the Festipod entities written per-document. */
export const ENTITY_TYPE = {
  event: 'http://festipod.org/Event',
  user: 'http://festipod.org/UserProfile',
  participation: 'http://festipod.org/Participation',
} as const;

const FP = 'http://festipod.org/';
const XSD = 'http://www.w3.org/2001/XMLSchema#';

/** An entity field as an RDF term (matches the SHEX datatype of the field). */
export type EntityTerm =
  | { kind: 'string'; value: string | undefined }
  | { kind: 'integer'; value: number | undefined }
  | { kind: 'float'; value: number | undefined }
  | { kind: 'boolean'; value: boolean | undefined }
  | { kind: 'iri'; value: string | undefined };

// --- term-builder shorthands (used by the callers to declare field types) ---
export const str = (value: string | undefined): EntityTerm => ({ kind: 'string', value });
export const int = (value: number | undefined): EntityTerm => ({ kind: 'integer', value });
export const flt = (value: number | undefined): EntityTerm => ({ kind: 'float', value });
export const bool = (value: boolean | undefined): EntityTerm => ({ kind: 'boolean', value });
export const iri = (value: string | undefined): EntityTerm => ({ kind: 'iri', value });

/** Render one term to its SPARQL object form (or null to skip the triple). */
function renderTerm(t: EntityTerm): string | null {
  if (t.value === undefined || t.value === null || t.value === '') return null;
  switch (t.kind) {
    case 'string':
      return `"${escapeLiteral(String(t.value))}"`;
    case 'integer':
      return `"${Math.trunc(t.value as number)}"^^<${XSD}integer>`;
    case 'float':
      return `"${t.value as number}"^^<${XSD}decimal>`;
    case 'boolean':
      return `"${t.value ? 'true' : 'false'}"^^<${XSD}boolean>`;
    case 'iri':
      // The reference IRIs are entity subject IRIs (coming back from a prior
      // write / the ORM). No pre-flight guard: the SDK validates at its own
      // door, and here the value only has to be a well-formed IRIREF body.
      return `<${escapeIri(String(t.value))}>`;
  }
}

/**
 * Persist a single-valued field of an existing entity: DELETE the old triple(s)
 * for `<subject> <fp:field> ?o` then INSERT the new term. Used for mutable fields
 * like `participantCount` — an in-place ORM mutation is LOCAL only and gets
 * overwritten when the reactive read re-syncs the doc from the broker (reverting
 * to the persisted value); persisting it via SPARQL makes the change durable and
 * the re-read consistent. `subject` is the entity `@id` (= its document NURI).
 */
export async function updateEntityField(
  graphNuri: NuriLike,
  subject: string,
  field: string,
  term: EntityTerm,
): Promise<void> {
  const sid = (await sessionPromise).session_id;
  const s = escapeIri(subject);
  const pred = `${FP}${field}`;
  const obj = renderTerm(term);
  // NO explicit `GRAPH <…>` wrapper: anchored to `graphNuri`, both the DELETE and
  // the INSERT target that doc's anchored DEFAULT graph — the exact graph the
  // anchored read queries. This no-GRAPH default-graph form is the CANONICAL SDK
  // write shape (same as writeEntity / registration.ts); SDK graph details live in
  // `@ng-eventually/polyfill`, not here. `docs.sparqlUpdate` validates the anchor at
  // its own door — `subject` only needs escaping, as it lands in an IRI position.
  if (obj === null) {
    // Clearing the field: there is nothing to put back, so the removal stands alone.
    await docs.sparqlUpdate(sid, `DELETE WHERE { <${s}> <${pred}> ?o }`, graphNuri);
    return;
  }
  // ONE update, not a DELETE followed by an INSERT. Sent as two, the field is
  // ABSENT between them, and a read landing in that window does not see "the old
  // value" — it sees NO value, which the read side turns into the field's empty
  // reading (0 for `participantCount`). The window is small and the reads are
  // pushed, so it shows up as a count that flickers to 0 for no reason anyone can
  // reproduce on demand.
  //
  // `DELETE … INSERT … WHERE` is ONE SPARQL modify operation — the surface takes
  // an update string and this is a single one, so nothing here invents a
  // transaction the SDK does not offer. The `OPTIONAL` is what makes it work on a
  // field that is not there yet: the WHERE still yields one solution (with `?o`
  // unbound, so the DELETE template drops out) and the INSERT applies. When the
  // field is present, every one of its triples is removed and the new one written
  // in the same operation.
  const update = `
    DELETE { <${s}> <${pred}> ?o }
    INSERT { <${s}> <${pred}> ${obj} }
    WHERE  { OPTIONAL { <${s}> <${pred}> ?o } }`;
  await docs.sparqlUpdate(sid, update, graphNuri);
}

/**
 * Write ONE entity as RDF into its OWN document (`graphNuri`, from
 * `createEntityDoc`). `typeIri` is the entity `@type`; `fields` maps LOCAL field
 * names (e.g. `title`, `participantCount`) to typed terms — each becomes the
 * predicate `http://festipod.org/<field>` with its term rendered per its SHEX
 * datatype. Empty/undefined values are skipped. Returns the subject IRI (the ORM
 * surfaces it as the entity's `@id`).
 */
export async function writeEntity(
  graphNuri: Nuri,
  typeIri: string,
  fields: Record<string, EntityTerm>,
): Promise<Nuri> {
  const sid = (await sessionPromise).session_id;
  // The entity IS its own document (one document per entity), so its subject IRI
  // is the DOCUMENT NURI itself (a `did:ng:…`). This gives the ORM a `did:ng:`
  // `@id` (what the @data assertions expect) and makes the entity self-addressing.
  const subject = graphNuri;
  const triples: string[] = [`a <${typeIri}>`];
  for (const [field, term] of Object.entries(fields)) {
    const obj = renderTerm(term);
    if (obj === null) continue;
    triples.push(`<${FP}${field}> ${obj}`);
  }
  // NO explicit `GRAPH <…>` wrapper: anchored to `graphNuri`, the write lands in
  // that doc's anchored DEFAULT graph — the exact graph the anchored read queries.
  // This is the CANONICAL SDK write shape (anchor scopes the write, no GRAPH clause;
  // same as updateEntityField / registration.ts); SDK graph details live in
  // `@ng-eventually/polyfill`, not here.
  const update = `
    INSERT DATA {
      <${escapeIri(subject)}> ${triples.join(' ;\n        ')} .
    }`;
  await docs.sparqlUpdate(sid, update, graphNuri);
  return subject;
}
